import { useBreakpointIndex } from '@tishman/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';

export type TPrivateEventsGalleryCarouselItem = {
  alt: string;
  asset: Queries.SanityImageAsset;
  caption?: string;
};

interface IScrollProps {
  scrollOffset: number;
  scrollUpdateWasRequested: boolean;
}

type TUseCarousel = (items: TPrivateEventsGalleryCarouselItem[]) => {
  beginning: boolean;
  end: boolean;
  getImageWidth: (index: number) => number;
  handleNext: () => void;
  handlePrevious: () => void;
  listRef: React.MutableRefObject<VariableSizeList | null>;
  onListScroll: ({ scrollOffset }: IScrollProps) => void;
  width: number;
};

interface IScrollContainer extends VariableSizeList {
  state: {
    scrollOffset: number;
  };
}

export const useCarousel: TUseCarousel = (items) => {
  const breakpointIndex = useBreakpointIndex();
  const isMobile = useMemo(() => breakpointIndex < 2, [breakpointIndex]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [beginning, setBeginning] = useState(true);
  const [end, setEnd] = useState(false);
  const listRef = useRef<IScrollContainer | null>(null);
  const carouselStep = 1;
  const [width, setWidth] = useState(0);

  const imageDimensions = items.map((image) => {
    return {
      width: image.asset.metadata?.dimensions?.width,
      height: image.asset.metadata?.dimensions?.height,
    };
  });

  useEffect(() => {
    const onResize = () => {
      if (window) {
        setWidth(window.innerWidth);
      }
    };

    onResize();

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handlePrevious = () => {
    if (activeIndex > 0) {
      const scrollContainer = listRef.current;

      if (scrollContainer) {
        // calculate the width of the previous carousel step images
        const scrollBy = imageDimensions
          .slice(Math.max(activeIndex - carouselStep, 0), activeIndex) // get the previous carousel step images
          .reduce(
            (acc, _, index) =>
              acc + getImageWidth(activeIndex - carouselStep + index),
            0,
          );

        // back to the start, add padding
        const isGoingToStart = activeIndex - carouselStep <= 0;

        const newScrollPosition = isGoingToStart
          ? 0
          : Math.max(scrollContainer.state.scrollOffset - scrollBy, 0);

        // Scroll to the new position
        scrollContainer.scrollTo(newScrollPosition);

        // update the index
        const newIndex = isGoingToStart
          ? 0
          : Math.max(activeIndex - carouselStep, 0);
        setActiveIndex(newIndex);
        setEnd(false); // Set end to false when moving backward

        // set beginning to true if we're at the start
        if (newIndex === 0) {
          setBeginning(true);
        }
      }
    }
  };

  const handleNext = () => {
    if (activeIndex < items.length - 1) {
      const scrollContainer = listRef.current;

      if (scrollContainer) {
        // calculate the width of the next carousel step images
        const scrollBy = imageDimensions
          .slice(activeIndex, activeIndex + carouselStep)
          .reduce(
            (acc, _, index) => acc + getImageWidth(activeIndex + index),
            0,
          );

        // move the list
        const newScrollPosition = scrollContainer.state.scrollOffset + scrollBy;

        // move to the new position
        scrollContainer.scrollTo(newScrollPosition);

        // update the index
        const newIndex = Math.min(activeIndex + carouselStep, items.length - 1);
        setActiveIndex(newIndex);
        setBeginning(false);
      }

      // if we're at the
      if (activeIndex + carouselStep >= items.length - 1) {
        setEnd(true);
      }
    }
  };

  const onListScroll = ({ scrollOffset }: IScrollProps) => {
    let cumulativeWidth = 0;
    let newIndex = 0;

    // calculate the index of the active image
    for (let i = 0; i < items.length; i++) {
      const width = getImageWidth(i);
      cumulativeWidth += width;

      if (cumulativeWidth > scrollOffset) {
        newIndex = i;
        break;
      }
    }

    setActiveIndex(newIndex);
    setBeginning(scrollOffset <= 0); // if scrollOffset <= 0 - the beginning

    // calculate the total width of all images
    const totalWidth = imageDimensions.reduce(
      (acc, _, i) => acc + getImageWidth(i),
      0,
    );
    const visibleWidth = isMobile ? width : 1368;
    const maxScrollOffset = totalWidth - visibleWidth;

    setEnd(scrollOffset >= maxScrollOffset - 100);
  };

  const getImageWidth = useCallback(
    (index: number) => {
      const height = isMobile ? 220 : 430;
      const image = imageDimensions[index];
      const aspectRatio = (image?.width || 0) / (image?.height || 1);

      const calculatedWidth = aspectRatio * height;
      const imageMinWidth = isMobile ? 240 : 360;
      const finalWidth = Math.max(
        Math.min(calculatedWidth, 645),
        imageMinWidth,
      );
      return finalWidth;
    },
    [isMobile, imageDimensions],
  );

  return {
    beginning,
    end,
    getImageWidth,
    handleNext,
    handlePrevious,
    listRef,
    onListScroll,
    width,
  };
};
